import { Trans } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { type FormEvent, useRef, useState } from 'react';
import { Mag } from '../components/mag.js';
import { magToast } from '../components/toaster.js';
import { TopNav } from '../components/top-nav.js';
import {
  type DesignerAssignedSlot,
  fetchDesignerAssignedSlots,
  uploadDesignerAssetForSlot,
} from '../lib/api.js';
import { meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

// [designer-role] Designer-only landing page.
//
// Shows every slot in 'awaiting_designer' across the brands the user
// is a designer on. Per slot: brand chip + channel + concept + caption
// preview + image brief + file picker. Submit uploads via the session-
// authed endpoint — no magic-link token needed when the designer has
// a magig account.
//
// Auto-redirect:
//   - Designer-only users land here from /home and /brands (the route
//     guards check `me.designerOnly`).
//   - Users with broader brand access (editor / manager / admin / owner)
//     see the full /home dashboard; this page stays reachable via
//     direct URL but isn't the default.

export const Route = createFileRoute('/designer/')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    return { me };
  },
  component: DesignerIndexPage,
});

function DesignerIndexPage() {
  const { me: _me } = Route.useRouteContext();
  const slotsQ = useQuery({
    queryKey: ['designer', 'me', 'slots'] as const,
    queryFn: fetchDesignerAssignedSlots,
    staleTime: 30_000,
  });

  return (
    <div className="min-h-dvh bg-[var(--paper)]">
      {/* [designer-role] Same TopNav as every other authenticated
          surface — it self-detects designer-only and renders the
          stripped-down chrome (wordmark + "designer" badge + bell +
          user menu). Designers feel like first-class citizens, not
          a different product. */}
      <TopNav active={null} />
      <main className="mx-auto max-w-[760px] px-4 py-8 sm:px-6 sm:py-10">
        <header className="mb-6">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            {'↘'} <Trans>design queue</Trans>
          </p>
          <h1 className="mt-1 font-display text-[28px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
            <Trans>what's waiting on you.</Trans>
          </h1>
          <p className="mt-2 text-[13px] text-[var(--ink)]/65">
            <Trans>
              every slot below is awaiting the asset you'll render. drop the file straight on the
              card — mag picks it up the moment it lands.
            </Trans>
          </p>
        </header>

        {slotsQ.isLoading ? (
          <Loading />
        ) : slotsQ.error ? (
          <ErrorCard message={slotsQ.error instanceof Error ? slotsQ.error.message : ''} />
        ) : (slotsQ.data?.length ?? 0) === 0 ? (
          <EmptyState />
        ) : (
          <ul className="flex flex-col gap-4">
            {slotsQ.data?.map((slot) => (
              <li key={slot.slotId}>
                <SlotCard slot={slot} />
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function Loading() {
  return (
    <p className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-6 text-center font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/45 shadow-[4px_4px_0_var(--spell)]">
      <Trans>reading your queue…</Trans>
    </p>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <p className="rounded-2xl border-[1.5px] border-[var(--pulse)]/55 bg-[var(--pulse)]/5 p-6 text-center mag-voice text-[13px] text-[var(--ink)]/80">
      <Trans>“something broke loading your queue — refresh in a sec.”</Trans>
      {message ? (
        <span className="mt-2 block font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          {message}
        </span>
      ) : null}
    </p>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-8 text-center shadow-[4px_4px_0_var(--spell)]">
      <Mag size={48} mood="casting" />
      <p className="mt-4 mag-voice text-[15px] leading-[1.5] text-[var(--ink)]/85">
        <Trans>
          “nothing waiting on you right now. i'll ping you the moment a slot's ready for an asset.”
        </Trans>
      </p>
    </div>
  );
}

function SlotCard({ slot }: { slot: DesignerAssignedSlot }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('pick a file first');
      return uploadDesignerAssetForSlot({ slotId: slot.slotId, file });
    },
    onSuccess: async () => {
      magToast.success('uploaded.', "mag's picking it up — the slot moves out of your queue.");
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      await refreshQueries(queryClient, ['designer', 'me', 'slots']);
    },
    onError: (err: Error) => {
      magToast.error("couldn't upload.", err.message);
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <article className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          ↘ {slot.brandName.toLowerCase()} · {slot.channelKind} · {slot.format}
        </p>
        <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <Trans>publishes {slot.scheduledForLabel}</Trans>
        </p>
      </div>

      <h2 className="mt-2 font-display text-[18px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]">
        {slot.concept}
      </h2>

      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        <Trans>↘ pillar · {slot.pillar.toLowerCase()}</Trans>
      </p>

      <section className="mt-4 rounded-xl border border-[var(--line)] bg-[var(--paper)] p-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
          {'↘'} <Trans>image brief</Trans>
        </p>
        <p className="mt-1 whitespace-pre-wrap text-[13.5px] leading-[1.5] text-[var(--ink)]">
          {slot.imageBrief}
        </p>
      </section>

      <section className="mt-3 rounded-xl border border-[var(--line)] bg-[var(--paper)] p-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
          {'↘'} <Trans>caption</Trans>
        </p>
        <p className="mt-1 whitespace-pre-wrap font-serif text-[14px] leading-[1.5] text-[var(--ink)]/85">
          {slot.captionPreview}
        </p>
      </section>

      <form onSubmit={handleSubmit} className="mt-4 flex flex-wrap items-center justify-end gap-3">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          disabled={mutation.isPending}
          className="text-[12.5px] text-[var(--ink)]/85 file:mr-3 file:rounded-md file:border file:border-[var(--ink)]/30 file:bg-white file:px-3 file:py-1.5 file:text-[12px] file:font-medium file:text-[var(--ink)] file:hover:bg-[var(--spell-soft)]/60"
        />
        <button
          type="submit"
          disabled={!file || mutation.isPending}
          className="btn disabled:opacity-50"
        >
          {mutation.isPending ? <Trans>uploading…</Trans> : <Trans>upload asset</Trans>}
          <span className="btn-arrow">→</span>
        </button>
      </form>
    </article>
  );
}
